import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Plus, Trash2, LayoutTemplate, Link2, ExternalLink, Save, GripVertical,
  ChevronUp, ChevronDown, Pencil, Flag, Check, X, Anchor, Diamond,
  Upload, Download, CalendarDays, RefreshCw, ListTree, Rows3, Zap, Columns3,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Gantt } from "@/components/Gantt";
import { EditCell } from "@/components/EditCell";
import { PlanOutline, predLabel, LinkDialog, TASK_STATUS as OUTLINE_STATUS } from "@/components/PlanOutline";
import { ColumnChooser } from "@/components/ColumnChooser";
import { COLUMN_BY_KEY, loadColumns, saveColumns, renderCell } from "@/lib/planColumns";
import { PlanImport } from "@/components/PlanImport";
import { PlanCalendarDialog } from "@/components/PlanCalendarDialog";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const TASK_STATUS = ["Not Started", "In Progress", "Complete", "Delayed", "On Hold"];
const BLANK = { name: "", stage: "", owner: "", start_date: "", end_date: "", percent_complete: 0, status: "Not Started", is_milestone: false, notes: "" };
const UNASSIGNED = "__unassigned__";

const Stat = ({ label, value, testId }) => (
  <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="mt-0.5 font-display text-lg font-semibold text-slate-900">{value}</div>
  </div>
);

export const Schedule = ({ projectId, stages: orgStages = [] }) => {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ template_id: "", start_date: "", replace_existing: false });
  const [saveTpl, setSaveTpl] = useState(null);
  const [plan, setPlan] = useState({ external_plan_url: "", external_plan_name: "" });
  const [loadError, setLoadError] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [newStage, setNewStage] = useState("");
  const [addingStage, setAddingStage] = useState(false);
  const [view, setView] = useState("outline");
  const [importOpen, setImportOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [showCritical, setShowCritical] = useState(true);
  const [colsOpen, setColsOpen] = useState(false);
  const [columnKeys, setColumnKeys] = useState(loadColumns);
  const [linking, setLinking] = useState(null);
  const [exporting, setExporting] = useState(false);
  const dragged = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/schedule`);
      setData(data);
      setPlan({ external_plan_url: data.external_plan_url || "", external_plan_name: data.external_plan_name || "" });
      setLoadError(null);
    } catch (e) {
      setLoadError(apiError(e.response?.data?.detail) || "Could not load the schedule");
    }
  }, [projectId]);

  const loadTemplates = useCallback(() => {
    api.get("/schedule-templates").then(({ data }) => setTemplates(data)).catch(() => setTemplates([]));
  }, []);

  useEffect(() => { load(); loadTemplates(); }, [load, loadTemplates]);

  const patchTask = async (task, patch, rebaseline = false) => {
    try {
      await api.patch(`/schedule/tasks/${task.id}`, { ...patch, rebaseline });
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      await load();
    }
  };

  const addTask = async (presetStage) => {
    setForm({ ...BLANK, stage: presetStage && presetStage !== UNASSIGNED ? presetStage : "" });
    setOpen(true);
  };

  const saveNewTask = async () => {
    if (!form.name.trim()) return toast.error("Task name is required");
    try {
      await api.post(`/projects/${projectId}/schedule/tasks`, {
        ...form,
        percent_complete: Number(form.percent_complete) || 0,
        order: (data?.tasks?.length || 0) + 1,
        stage: form.stage || null,
        owner: form.owner || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        baseline_start: form.start_date || null,
        baseline_end: form.end_date || form.start_date || null,
        notes: form.notes || null,
      });
      setOpen(false);
      await load();
      toast.success("Task added");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await api.delete(`/schedule/tasks/${t.id}`);
      await load();
      toast.success("Task deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const applyTemplate = async () => {
    if (!tplForm.template_id) return toast.error("Choose a template");
    if (!tplForm.start_date) return toast.error("Choose the plan start date");
    try {
      const { data: res } = await api.post(`/projects/${projectId}/schedule/apply-template`, tplForm);
      toast.success(`${res.created} tasks created from ${res.template} — everything below is editable`);
      setTplOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const savePlanLink = async () => {
    try {
      await api.put(`/projects/${projectId}/schedule/external-plan`, plan);
      await load();
      toast.success(plan.external_plan_url ? "Plan link saved" : "Plan link removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const persistOrder = async (ordered) => {
    setData((d) => ({ ...d, tasks: ordered }));
    try {
      await api.put(`/projects/${projectId}/schedule/reorder`, { task_ids: ordered.map((t) => t.id) });
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      await load();
    }
  };

  const moveTask = (task, dir) => {
    const list = [...data.tasks];
    const i = list.findIndex((x) => x.id === task.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    persistOrder(list);
  };

  const onDrop = async (target) => {
    const src = dragged.current;
    dragged.current = null;
    if (!src || src.id === target.id) return;
    const list = data.tasks.filter((t) => t.id !== src.id);
    const at = list.findIndex((t) => t.id === target.id);
    list.splice(at, 0, { ...src, stage: target.stage ?? null });
    if ((src.stage ?? null) !== (target.stage ?? null)) {
      await api.patch(`/schedule/tasks/${src.id}`, { stage: target.stage ?? "", rebaseline: false }).catch(() => {});
    }
    await persistOrder(list);
  };

  const saveStageOrder = async (list) => {
    try {
      await api.put(`/projects/${projectId}/schedule/stages`, { stages: list });
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const moveStage = (stage, dir) => {
    const list = [...planStages];
    const i = list.indexOf(stage);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    saveStageOrder(list);
    const reordered = list.flatMap((s) => data.tasks.filter((t) => (t.stage || null) === s))
      .concat(data.tasks.filter((t) => !t.stage));
    persistOrder(reordered);
  };

  const commitRename = async () => {
    const next = (renaming?.value || "").trim();
    if (!next) return toast.error("The stage name cannot be empty");
    try {
      await api.post(`/projects/${projectId}/schedule/stages/rename`, { old_name: renaming.stage, new_name: next });
      setRenaming(null);
      await load();
      toast.success("Stage renamed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeStage = async (stage) => {
    const count = data.tasks.filter((t) => (t.stage || null) === stage).length;
    const deleteTasks = count > 0 && window.confirm(
      `"${stage}" has ${count} task(s).\n\nOK = delete the stage and its tasks.\nCancel = keep the tasks but leave them unassigned.`,
    );
    try {
      await api.post(`/projects/${projectId}/schedule/stages/delete`, { stage, mode: deleteTasks ? "delete_tasks" : "unassign" });
      await load();
      toast.success(deleteTasks ? "Stage and its tasks deleted" : "Stage removed, tasks kept");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addStage = async () => {
    const name = newStage.trim();
    if (!name) return;
    if (planStages.includes(name)) return toast.error("That stage is already in the plan");
    await saveStageOrder([...planStages, name]);
    setNewStage("");
    setAddingStage(false);
    toast.success(`"${name}" added — drag tasks into it or use its Add Task button`);
  };

  const setOutline = async (ids, direction) => {
    try {
      await api.post(`/projects/${projectId}/schedule/outline`, { task_ids: ids, direction });
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveCalendar = async (cal) => {
    try {
      await api.put(`/projects/${projectId}/schedule/calendar`, cal);
      setCalOpen(false);
      await load();
      toast.success("Working calendar saved — dates recalculated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const baselineAll = async () => {
    if (!window.confirm("Set the baseline on every task from its current dates?")) return;
    try {
      const { data: res } = await api.post(`/projects/${projectId}/schedule/baseline`);
      await load();
      toast.success(`${res.baselined} tasks baselined`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const recalculate = async () => {
    try {
      const { data: res } = await api.post(`/projects/${projectId}/schedule/recalculate`);
      await load();
      toast.success(`Schedule recalculated — ${res.updated} task(s) moved, ${res.critical_count} on the critical path`);
      (res.warnings || []).forEach((w) => toast.warning(w));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const exportPlan = async (format) => {
    setExporting(true);
    try {
      const res = await api.get(`/projects/${projectId}/schedule/export`, {
        params: { format }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plan.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Plan exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const doSaveAsTemplate = async () => {
    if (!saveTpl.name.trim()) return toast.error("Give the template a name");
    try {
      const { data: res } = await api.post(`/projects/${projectId}/schedule/save-as-template`, saveTpl);
      setSaveTpl(null);
      loadTemplates();
      toast.success(`Saved "${res.name}" with ${res.task_count} tasks`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (loadError) {
    return (
      <div data-testid="schedule-load-error" className="rounded-md border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-700">
        {loadError}
        <div className="mt-3">
          <Button size="sm" variant="outline" data-testid="schedule-retry-btn" onClick={load}>Try again</Button>
        </div>
      </div>
    );
  }
  if (!data) return <p className="py-8 text-center text-sm text-slate-400">Loading schedule…</p>;

  const s = data.summary;
  const planStages = data.stages || [];
  const stageOptions = Array.from(new Set([...planStages, ...orgStages.map((x) => x.value ?? x)]));
  const settings = data.settings || { currency: "£", hours_per_day: 8, track_cost: true };
  const visibleColumnKeys = columnKeys.filter((k) => settings.track_cost !== false || !COLUMN_BY_KEY[k]?.cost);
  const activeColumns = visibleColumnKeys.map((k) => COLUMN_BY_KEY[k]).filter(Boolean);
  const cellHelpers = {
    patch: patchTask, EditCell, stageOptions, currency: settings.currency,
    taskStatus: OUTLINE_STATUS, onLinks: setLinking, predLabel: (t) => predLabel(data.tasks, t),
  };

  const applyColumns = (keys) => { setColumnKeys(keys); saveColumns(keys); };

  const savePlanSettings = async (cfg) => {
    try {
      await api.put(`/projects/${projectId}/schedule/settings`, cfg);
      await load();
      toast.success("Plan settings saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const grouped = [    ...planStages.map((st) => ({ stage: st, tasks: data.tasks.filter((t) => (t.stage || null) === st) })),
    { stage: UNASSIGNED, tasks: data.tasks.filter((t) => !t.stage) },
  ].filter((g) => g.stage !== UNASSIGNED || g.tasks.length > 0);

  const Row = ({ t }) => (
    <tr
      data-testid={`task-row-${t.id}`}
      draggable
      onDragStart={() => { dragged.current = t; }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onDrop(t)}
      className="group transition-colors hover:bg-slate-50/80"
    >
      <td className="w-8 px-2 py-2 align-middle">
        <GripVertical size={13} data-testid={`drag-task-${t.id}`} className="cursor-grab text-slate-300 group-hover:text-slate-500" />
      </td>
      <td className="min-w-[220px] px-2 py-2">
        <div className="flex items-center gap-1.5">
          <button
            data-testid={`toggle-milestone-${t.id}`}
            onClick={() => patchTask(t, { is_milestone: !t.is_milestone })}
            title={t.is_milestone ? "Make this a task" : "Make this a milestone"}
            className={t.is_milestone ? "text-amber-500" : "text-slate-200 hover:text-slate-400"}
          >
            <Diamond size={12} fill={t.is_milestone ? "currentColor" : "none"} />
          </button>
          <EditCell testId={`cell-name-${t.id}`} value={t.name} onCommit={(v) => patchTask(t, { name: v })} className="font-medium text-slate-800" placeholder="Untitled task" />
          {showCritical && t.is_critical && (
            <span data-testid={`critical-flag-${t.id}`} title="On the critical path" className="shrink-0 rounded bg-red-50 px-1 text-[9px] font-bold uppercase text-red-600">crit</span>
          )}
        </div>
      </td>
      {activeColumns.map((c) => (
        <td key={c.key} className={`whitespace-nowrap px-2 py-2 ${c.width || ""}`}>
          {renderCell(c, t, cellHelpers)}
        </td>
      ))}
      <td className="px-2 py-2">
        <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button data-testid={`move-up-${t.id}`} onClick={() => moveTask(t, -1)} aria-label="Move up" title="Move up" className="text-slate-400 hover:text-slate-800"><ChevronUp size={14} /></button>
          <button data-testid={`move-down-${t.id}`} onClick={() => moveTask(t, 1)} aria-label="Move down" title="Move down" className="text-slate-400 hover:text-slate-800"><ChevronDown size={14} /></button>
          <button data-testid={`rebaseline-task-${t.id}`} onClick={() => patchTask(t, {}, true)} aria-label="Re-baseline" title="Re-baseline to the current dates" className="text-slate-400 hover:text-indigo-600"><Anchor size={13} /></button>
          <button data-testid={`delete-task-${t.id}`} onClick={() => removeTask(t)} aria-label="Delete task" title="Delete task" className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6" data-testid="schedule-section">
      <div className="panel p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-semibold text-slate-800">Project Plan</h3>
            <p className="text-xs text-slate-500">
              Click any cell to edit it. Drag rows to reorder or move them between stages. Templates are a starting point, not a fixed structure.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" data-testid="import-plan-btn" onClick={() => setImportOpen(true)} className="gap-1.5">
              <Upload size={14} /> Import Plan
            </Button>
            <div className="flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-1">
              <Download size={13} className="text-slate-400" />
              {["xlsx", "csv", "xml", "pdf"].map((f) => (
                <button key={f} data-testid={`export-plan-${f}`} disabled={exporting} onClick={() => exportPlan(f)}
                  title={f === "xml" ? "MS Project XML — opens directly in MS Project" : `Export as ${f.toUpperCase()}`}
                  className="rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50">
                  {f}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" data-testid="columns-btn" onClick={() => setColsOpen(true)} className="gap-1.5">
              <Columns3 size={14} /> Columns
            </Button>
            <Button size="sm" variant="outline" data-testid="plan-calendar-btn" onClick={() => setCalOpen(true)} className="gap-1.5">
              <CalendarDays size={14} /> Calendar
            </Button>
            <Button size="sm" variant="outline" data-testid="recalculate-btn" onClick={recalculate} className="gap-1.5">
              <RefreshCw size={14} /> Recalculate
            </Button>
            <Button size="sm" variant="outline" data-testid="baseline-all-btn" onClick={baselineAll} className="gap-1.5">
              <Anchor size={14} /> Baseline All
            </Button>
            <Button size="sm" variant="outline" data-testid="apply-template-btn" onClick={() => setTplOpen(true)} className="gap-1.5">
              <LayoutTemplate size={14} /> Apply Template
            </Button>
            <Button size="sm" variant="outline" data-testid="save-as-template-btn" onClick={() => setSaveTpl({ name: "", framework: "", description: "" })} className="gap-1.5">
              <Save size={14} /> Save as Template
            </Button>
            <Button size="sm" data-testid="add-task-btn" onClick={() => addTask()} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Plus size={14} /> Add Task
            </Button>
          </div>
        </div>

        {data.warnings?.length > 0 && (
          <div data-testid="plan-warnings" className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {data.warnings.join(" · ")}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          <Stat testId="sched-stat-tasks" label="Tasks" value={s.task_count} />
          <Stat testId="sched-stat-milestones" label="Milestones" value={s.milestone_count} />
          <Stat testId="sched-stat-critical" label="Critical" value={s.critical_count ?? 0} />
          <Stat testId="sched-stat-percent" label="% Complete" value={`${s.percent_complete}%`} />
          <Stat testId="sched-stat-complete" label="Done" value={s.complete_count} />
          <Stat testId="sched-stat-start" label="Plan Start" value={s.start_date || "—"} />
          <Stat testId="sched-stat-end" label="Plan End" value={s.end_date || "—"} />
        </div>

        {s.slipped_count > 0 && (
          <div data-testid="sched-slippage-banner" className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span className="font-semibold">{s.slipped_count} task{s.slipped_count === 1 ? "" : "s"} behind baseline:</span>{" "}
            {s.slipped_tasks.join(", ")}
          </div>
        )}
      </div>

      <div className="panel p-5" data-testid="gantt-panel">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-base font-semibold text-slate-800">Timeline</h3>
          <button data-testid="toggle-critical-btn" onClick={() => setShowCritical((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
              showCritical ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            <Zap size={12} /> Critical path {showCritical ? "on" : "off"}
          </button>
        </div>
        <Gantt tasks={data.tasks} showCritical={showCritical} />
      </div>

      <div className="panel overflow-x-auto" data-testid="task-table">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-display text-sm font-semibold text-slate-800">
              {view === "outline" ? "Outline & Dependencies" : "Stages & Tasks"}
            </h3>
            <div className="flex items-center gap-1 rounded-md bg-slate-100 p-0.5">
              <button data-testid="view-outline-btn" onClick={() => setView("outline")}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold transition-colors ${view === "outline" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                <ListTree size={12} /> Outline
              </button>
              <button data-testid="view-stage-btn" onClick={() => setView("stage")}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold transition-colors ${view === "stage" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                <Rows3 size={12} /> By stage
              </button>
            </div>
          </div>
          {addingStage ? (
            <div className="flex items-center gap-2">
              <Input data-testid="new-stage-input" autoFocus value={newStage} placeholder="New stage name" className="h-8 w-48"
                onChange={(e) => setNewStage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addStage()} />
              <button data-testid="confirm-add-stage-btn" onClick={addStage} aria-label="Add stage" title="Add stage" className="text-emerald-600 hover:text-emerald-800"><Check size={16} /></button>
              <button data-testid="cancel-add-stage-btn" onClick={() => { setAddingStage(false); setNewStage(""); }} aria-label="Cancel" title="Cancel" className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
          ) : (
            <Button size="sm" variant="outline" data-testid="add-stage-btn" onClick={() => setAddingStage(true)} className="h-8 gap-1.5">
              <Plus size={13} /> Add Stage
            </Button>
          )}
        </div>

        {data.tasks.length === 0 && planStages.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">
            No tasks yet. Add a task, apply a template, or import a plan from MS Project, Excel or CSV.
          </p>
        ) : view === "outline" ? (
          <PlanOutline
            tasks={data.tasks}
            stageOptions={stageOptions}
            columns={visibleColumnKeys}
            currency={settings.currency}
            showCritical={showCritical}
            onPatch={patchTask}
            onOutline={setOutline}
            onDelete={removeTask}
          />
        ) : (
          <table className="w-full text-sm" style={{ minWidth: `${380 + activeColumns.length * 96}px` }}>
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-2 py-2" />
                <th className="px-2 py-2 font-semibold">Task</th>
                {activeColumns.map((c) => <th key={c.key} className={`whitespace-nowrap px-2 py-2 font-semibold ${c.width || ""}`}>{c.label}</th>)}
                <th className="px-2 py-2" />
              </tr>
            </thead>
            {grouped.map((g) => (
              <tbody key={g.stage} className="divide-y divide-slate-100" data-testid={`stage-group-${g.stage}`}>
                <tr className="bg-slate-50/70">
                  <td colSpan={activeColumns.length + 3} className="px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      {renaming?.stage === g.stage ? (
                        <div className="flex items-center gap-2">
                          <Input data-testid={`rename-stage-input-${g.stage}`} autoFocus value={renaming.value} className="h-7 w-52"
                            onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(null); }} />
                          <button data-testid={`confirm-rename-${g.stage}`} onClick={commitRename} aria-label="Save name" title="Save" className="text-emerald-600 hover:text-emerald-800"><Check size={15} /></button>
                          <button onClick={() => setRenaming(null)} aria-label="Cancel rename" title="Cancel" className="text-slate-400 hover:text-slate-700"><X size={15} /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Flag size={13} className="text-slate-400" />
                          <span className="font-display text-sm font-semibold text-slate-800">
                            {g.stage === UNASSIGNED ? "Unassigned" : g.stage}
                          </span>
                          <span className="text-[11px] text-slate-400">{g.tasks.length} task{g.tasks.length === 1 ? "" : "s"}</span>
                        </div>
                      )}
                      {g.stage !== UNASSIGNED && !renaming && (
                        <div className="flex items-center gap-2">
                          <button data-testid={`add-task-to-${g.stage}`} onClick={() => addTask(g.stage)} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900">+ Add task</button>
                          <button data-testid={`stage-up-${g.stage}`} onClick={() => moveStage(g.stage, -1)} aria-label="Move stage up" title="Move stage up" className="text-slate-400 hover:text-slate-800"><ChevronUp size={14} /></button>
                          <button data-testid={`stage-down-${g.stage}`} onClick={() => moveStage(g.stage, 1)} aria-label="Move stage down" title="Move stage down" className="text-slate-400 hover:text-slate-800"><ChevronDown size={14} /></button>
                          <button data-testid={`rename-stage-${g.stage}`} onClick={() => setRenaming({ stage: g.stage, value: g.stage })} aria-label="Rename stage" title="Rename stage" className="text-slate-400 hover:text-slate-800"><Pencil size={13} /></button>
                          <button data-testid={`delete-stage-${g.stage}`} onClick={() => removeStage(g.stage)} aria-label="Delete stage" title="Delete stage" className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
                {g.tasks.length === 0 ? (
                  <tr onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop({ id: "__none__", stage: g.stage === UNASSIGNED ? null : g.stage })}>
                    <td colSpan={activeColumns.length + 3} className="px-6 py-3 text-xs text-slate-400">No tasks in this stage yet — drag one here or use “Add task”.</td>
                  </tr>
                ) : g.tasks.map((t) => <Row key={t.id} t={t} />)}
              </tbody>
            ))}
          </table>
        )}
      </div>

      <div className="panel p-5" data-testid="external-plan-panel">
        <h3 className="font-display text-base font-semibold text-slate-800">External Plan</h3>
        <p className="mt-1 text-xs text-slate-500">
          Link an MS Project, Planner or Smartsheet plan. To store the file itself, upload it on the Documents tab.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className="field-label">Plan URL</label>
            <Input data-testid="external-plan-url-input" value={plan.external_plan_url} onChange={(e) => setPlan({ ...plan, external_plan_url: e.target.value })} placeholder="https://…" />
          </div>
          <div>
            <label className="field-label">Label</label>
            <Input data-testid="external-plan-name-input" value={plan.external_plan_name} onChange={(e) => setPlan({ ...plan, external_plan_name: e.target.value })} placeholder="e.g. Master plan (MS Project)" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" data-testid="save-external-plan-btn" onClick={savePlanLink} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Save size={14} /> Save Link
          </Button>
          {data.external_plan_url && (
            <a data-testid="open-external-plan-link" href={data.external_plan_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 underline-offset-2 hover:underline">
              <ExternalLink size={14} /> {data.external_plan_name || "Open plan"}
            </a>
          )}
          {!data.external_plan_url && <span className="inline-flex items-center gap-1.5 text-xs text-slate-400"><Link2 size={13} /> No plan linked</span>}
        </div>
      </div>

      <PlanImport open={importOpen} onOpenChange={setImportOpen} projectId={projectId}
        onImported={() => { load(); loadTemplates(); }} />
      <ColumnChooser open={colsOpen} onOpenChange={setColsOpen} selected={columnKeys}
        onApply={applyColumns} settings={settings} onSaveSettings={savePlanSettings} />
      <LinkDialog task={linking} tasks={data.tasks} onClose={() => setLinking(null)}
        onSave={(links) => { patchTask(linking, { predecessors: links, constraint_mode: "auto" }); setLinking(null); }} />
      {calOpen && (
        <PlanCalendarDialog open={calOpen} onOpenChange={setCalOpen} calendar={data.calendar} onSave={saveCalendar} />
      )}


      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="task-dialog" className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <DialogDescription>Once added, every field can be edited straight in the grid. Milestones only need a start date.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="field-label">Task Name *</label><Input data-testid="task-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="field-label">Stage</label><Select testId="task-stage-select" value={form.stage} onChange={(v) => setForm({ ...form, stage: v })} options={stageOptions} placeholder="Select stage" /></div>
            <div><label className="field-label">Owner</label><Input data-testid="task-owner-input" value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
            <div><label className="field-label">Start Date</label><Input data-testid="task-start-input" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><label className="field-label">End Date</label><Input data-testid="task-end-input" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            <div><label className="field-label">% Complete</label><Input data-testid="task-percent-input" type="number" min="0" max="100" value={form.percent_complete ?? 0} onChange={(e) => setForm({ ...form, percent_complete: e.target.value })} /></div>
            <div><label className="field-label">Status</label><Select testId="task-status-select" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={TASK_STATUS} placeholder="Select" /></div>
            <label className="flex items-center gap-2 self-end text-sm text-slate-700">
              <input data-testid="task-milestone-checkbox" type="checkbox" className="h-4 w-4" checked={!!form.is_milestone} onChange={(e) => setForm({ ...form, is_milestone: e.target.checked })} />
              This is a milestone
            </label>
            <div className="sm:col-span-2"><label className="field-label">Notes</label><Textarea data-testid="task-notes-input" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="task-cancel-btn" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="task-save-btn" onClick={saveNewTask} style={{ backgroundColor: "var(--brand-primary)" }}>Add Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent data-testid="apply-template-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply a Schedule Template</DialogTitle>
            <DialogDescription>Tasks are dated from your start date using each template task&rsquo;s offset and duration, then baselined. Everything stays fully editable afterwards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="field-label">Template</label>
              <Select testId="template-select" value={tplForm.template_id} onChange={(v) => setTplForm({ ...tplForm, template_id: v })} options={templates.map((t) => ({ value: t.id, label: `${t.name} (${t.tasks?.length || 0} tasks)` }))} placeholder="Choose template" />
            </div>
            <div>
              <label className="field-label">Plan Start Date</label>
              <Input data-testid="template-start-input" type="date" value={tplForm.start_date} onChange={(e) => setTplForm({ ...tplForm, start_date: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="template-replace-checkbox" type="checkbox" className="h-4 w-4" checked={tplForm.replace_existing} onChange={(e) => setTplForm({ ...tplForm, replace_existing: e.target.checked })} />
              Replace the existing tasks on this plan
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="template-cancel-btn" onClick={() => setTplOpen(false)}>Cancel</Button>
            <Button data-testid="template-apply-btn" onClick={applyTemplate} style={{ backgroundColor: "var(--brand-primary)" }}>Apply Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!saveTpl} onOpenChange={(o) => !o && setSaveTpl(null)}>
        <DialogContent data-testid="save-template-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save this plan as a template</DialogTitle>
            <DialogDescription>Task dates become offsets and durations from the earliest task, so the template can be applied to any future start date.</DialogDescription>
          </DialogHeader>
          {saveTpl && (
            <div className="space-y-4">
              <div><label className="field-label">Template Name *</label><Input data-testid="save-template-name-input" value={saveTpl.name} onChange={(e) => setSaveTpl({ ...saveTpl, name: e.target.value })} placeholder="e.g. Tailored Delivery Plan" /></div>
              <div><label className="field-label">Framework (optional)</label><Input data-testid="save-template-framework-input" value={saveTpl.framework} onChange={(e) => setSaveTpl({ ...saveTpl, framework: e.target.value })} /></div>
              <div><label className="field-label">Description</label><Textarea data-testid="save-template-description-input" rows={2} value={saveTpl.description} onChange={(e) => setSaveTpl({ ...saveTpl, description: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="save-template-cancel-btn" onClick={() => setSaveTpl(null)}>Cancel</Button>
            <Button data-testid="save-template-confirm-btn" onClick={doSaveAsTemplate} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Save size={14} /> Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
